import { db } from '../config/firebase';
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import { UserPlan } from '../types'; // Import UserPlan type
import { getModelById } from '../models'; // Import getModelById

interface RateLimitData {
    date: string;
    count: number;
    plan: UserPlan; // Add plan field
    lastUpdated: Timestamp;
    // Track Pro model usage separately for Pro users
    proModelsUsage?: number;
    proModelsResetDate?: string; // Monthly reset date for Pro models
}

// Collection name for rate limits
const COLLECTION_NAME = 'rateLimits';
const FREE_LIMIT = 10;
const PLUS_LIMIT = 75;
const PRO_MODELS_MONTHLY_LIMIT = 200; // Monthly limit for Pro users using Pro models

/**
 * Get the user's plan and usage data from Firestore
 */
const getUserRateLimitData = async (userId: string): Promise<RateLimitData | null> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, userId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            console.log(`No rate limit record found for user ${userId}. Creating default (Free plan).`);
            // Create a default record if none exists
            const defaultData: RateLimitData = {
                date: new Date().toISOString().split('T')[0],
                count: 0,
                plan: 'free', // Default to free plan
                lastUpdated: serverTimestamp() as Timestamp,
                proModelsUsage: 0,
                proModelsResetDate: getProModelsResetDate()
            };
            await setDoc(docRef, defaultData);
            return defaultData;
        }

        // Ensure plan field exists, default to 'free' if missing
        const data = docSnap.data() as RateLimitData;
        if (!data.plan) {
            data.plan = 'free';
        }

        // Ensure proModelsUsage field exists for Pro users
        if (data.plan === 'pro' && data.proModelsUsage === undefined) {
            data.proModelsUsage = 0;
            data.proModelsResetDate = getProModelsResetDate();
        }

        return data;

    } catch (error) {
        console.error(`Firestore Error: Failed to get rate limit data for user ${userId}.`, error);
        return null; // Return null on error
    }
};

/**
 * Get the date when Pro models usage should reset (1st of next month)
 */
const getProModelsResetDate = (): string => {
    const today = new Date();
    // Set to first day of next month
    const resetDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return resetDate.toISOString().split('T')[0];
};

/**
 * Get the current usage count for today
 */
export const getTodayUsage = async (userId: string): Promise<number> => {
    const data = await getUserRateLimitData(userId);
    if (!data) {
        return 0; // Assume 0 usage if fetch fails
    }

    const today = new Date().toISOString().split('T')[0];
    return data.date === today ? data.count : 0;
};

/**
 * Get the user's current plan
 */
export const getUserPlan = async (userId: string): Promise<UserPlan> => {
    const data = await getUserRateLimitData(userId);
    return data?.plan || 'free'; // Default to free if fetch fails or plan missing
};

/**
 * Get Pro models usage for Pro users
 */
export const getProModelsUsage = async (userId: string): Promise<number> => {
    const data = await getUserRateLimitData(userId);
    if (!data || data.plan !== 'pro') {
        return 0;
    }

    // Check if we need to reset due to month change
    const today = new Date().toISOString().split('T')[0];
    const resetDate = data.proModelsResetDate || getProModelsResetDate();

    if (new Date(today) >= new Date(resetDate)) {
        // Past reset date, should reset counter
        await resetProModelsUsage(userId);
        return 0;
    }

    return data.proModelsUsage || 0;
};

/**
 * Reset Pro models usage counter (happens monthly)
 */
const resetProModelsUsage = async (userId: string): Promise<void> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, userId);
        const dataToUpdate: Partial<RateLimitData> = {
            proModelsUsage: 0,
            proModelsResetDate: getProModelsResetDate(),
            lastUpdated: serverTimestamp() as Timestamp
        };
        await setDoc(docRef, dataToUpdate, { merge: true });
        console.log(`Firestore: Reset Pro models usage for user ${userId}.`);
    } catch (error) {
        console.error(`Firestore Error: Failed to reset Pro models usage for user ${userId}.`, error);
    }
};

/**
 * Increment the usage count based on model type
 */
export const incrementUsage = async (userId: string, modelId: string): Promise<number> => {
    const currentData = await getUserRateLimitData(userId);
    if (!currentData) {
        console.error(`Firestore Error: No rate limit data for user ${userId}.`);
        return 0;
    }

    const currentPlan = currentData.plan || 'free';
    const today = new Date().toISOString().split('T')[0];

    // Get model information
    const model = getModelById(modelId);
    const modelPlanRequired = model?.planRequired || 'free';

    try {
        const dataToUpdate: Partial<RateLimitData> = {
            date: today,
            plan: currentPlan,
            lastUpdated: serverTimestamp() as Timestamp
        };

        // Handle different update logic based on plan and model type
        if (currentPlan === 'pro' && modelPlanRequired === 'pro') {
            // Pro user using Pro model - update Pro models usage
            const currentProUsage = currentData.proModelsUsage || 0;
            dataToUpdate.proModelsUsage = currentProUsage + 1;

            // Check if we need to reset due to month change
            const resetDate = currentData.proModelsResetDate || getProModelsResetDate();
            if (new Date(today) >= new Date(resetDate)) {
                dataToUpdate.proModelsUsage = 1; // Reset and count this usage
                dataToUpdate.proModelsResetDate = getProModelsResetDate();
            }
        } else if (currentPlan === 'free') {
            // Free user - increment daily usage regardless of model
            const currentCount = currentData.date === today ? (currentData.count || 0) : 0;
            dataToUpdate.count = currentCount + 1;
        } else if (currentPlan === 'plus' && modelPlanRequired === 'pro') {
            // Plus user trying to use Pro model - not allowed, but track anyway
            const currentCount = currentData.date === today ? (currentData.count || 0) : 0;
            dataToUpdate.count = currentCount + 1;
        }

        const docRef = doc(db, COLLECTION_NAME, userId);
        await setDoc(docRef, dataToUpdate, { merge: true });

        // Return appropriate count based on user plan and model type
        if (currentPlan === 'pro' && modelPlanRequired === 'pro') {
            return dataToUpdate.proModelsUsage || 0;
        } else if (currentPlan === 'free' || (currentPlan === 'plus' && modelPlanRequired === 'pro')) {
            return dataToUpdate.count || 0;
        }

        return 0; // No limit for Plus users on Plus models or Pro users on Plus models
    } catch (error) {
        console.error(`Firestore Error: Failed to increment usage for user ${userId}, model ${modelId}.`, error);
        return 0;
    }
};

/**
 * Get the message limit based on the user's plan
 */
export const getMessageLimit = (plan: UserPlan): number => {
    switch (plan) {
        case 'pro':
            return PRO_MODELS_MONTHLY_LIMIT; // For Pro models only
        case 'plus':
            return PLUS_LIMIT; // For daily usage of free models only
        case 'free':
        default:
            return FREE_LIMIT;
    }
};

/**
 * Get remaining Pro model usages for Pro users (monthly pool)
 */
export const getRemainingProModelMessages = async (userId: string): Promise<number> => {
    const userPlan = await getUserPlan(userId);
    if (userPlan !== 'pro') {
        return 0; // Only Pro users have a Pro model message limit
    }

    const proModelsUsage = await getProModelsUsage(userId);
    return Math.max(0, PRO_MODELS_MONTHLY_LIMIT - proModelsUsage);
};

/**
 * Check if the user has reached their limit for a specific model
 */
export const hasReachedLimit = async (userId: string, modelId: string): Promise<boolean> => {
    try {
        const userData = await getUserRateLimitData(userId);
        if (!userData) return true; // Assume limit reached if data fetch fails

        const userPlan = userData.plan;
        const model = getModelById(modelId);
        const modelPlanRequired = model?.planRequired || 'free';

        // Models access rules based on plan and model requirements

        // Free plan users
        if (userPlan === 'free') {
            if (modelPlanRequired === 'plus' || modelPlanRequired === 'pro') {
                console.log(`User ${userId} (Free) cannot access ${modelPlanRequired} model ${modelId}`);
                return true; // No access to Plus or Pro models
            }

            // Check against free plan daily limit
            const currentUsage = userData.date === new Date().toISOString().split('T')[0] ? userData.count : 0;
            return currentUsage >= FREE_LIMIT;
        }

        // Plus plan users
        if (userPlan === 'plus') {
            if (modelPlanRequired === 'pro') {
                console.log(`User ${userId} (Plus) cannot access Pro model ${modelId}`);
                return true; // No access to Pro models
            }

            if (modelPlanRequired === 'plus') {
                return false; // Unlimited access to Plus models
            }

            // For free models, check against plus plan daily limit
            const currentUsage = userData.date === new Date().toISOString().split('T')[0] ? userData.count : 0;
            return currentUsage >= PLUS_LIMIT;
        }

        // Pro plan users
        if (userPlan === 'pro') {
            if (modelPlanRequired === 'plus' || modelPlanRequired === 'free') {
                return false; // Unlimited access to Plus and Free models
            }

            // For Pro models, check against monthly limit
            const today = new Date().toISOString().split('T')[0];
            const resetDate = userData.proModelsResetDate || getProModelsResetDate();

            // Check if we need to reset due to month change
            if (new Date(today) >= new Date(resetDate)) {
                await resetProModelsUsage(userId);
                return false; // After reset, limit not reached
            }

            const proModelsUsage = userData.proModelsUsage || 0;
            return proModelsUsage >= PRO_MODELS_MONTHLY_LIMIT;
        }

        return true; // Default to limit reached for other cases

    } catch (error) {
        console.error(`Firestore Error: Failed to check rate limit for user ${userId}, model ${modelId}. Assuming limit reached.`, error);
        return true; // Default to true (limit reached) if check fails
    }
};

/**
 * Reset the usage count (mainly for testing) - keeps the current plan
 */
export const resetUsage = async (userId: string): Promise<void> => {
    try {
        const currentPlan = await getUserPlan(userId);
        const today = new Date().toISOString().split('T')[0];

        const data: RateLimitData = {
            date: today,
            count: 0,
            plan: currentPlan, // Keep existing plan
            lastUpdated: serverTimestamp() as Timestamp,
            proModelsUsage: 0,
            proModelsResetDate: getProModelsResetDate()
        };

        const docRef = doc(db, COLLECTION_NAME, userId);
        await setDoc(docRef, data); // Overwrite completely
        console.log(`Firestore: Reset rate limit count for user ${userId} (Plan: ${currentPlan}).`);
    } catch (error) {
        console.error(`Firestore Error: Failed to reset rate limit for user ${userId}.`, error);
    }
};

/**
 * Get remaining messages for today based on plan
 */
export const getRemainingMessages = async (userId: string): Promise<number> => {
    try {
        const userData = await getUserRateLimitData(userId);
        if (!userData) return 0; // Assume 0 remaining if fetch fails

        const userPlan = userData.plan;

        // Pro and Plus users don't have daily limits for models of their tier or below
        if (userPlan === 'pro') {
            // Only track Pro model usage limit
            const proModelsUsage = userData.proModelsUsage || 0;
            return Math.max(0, PRO_MODELS_MONTHLY_LIMIT - proModelsUsage);
        }

        // Check standard daily limit based on plan
        const limit = getMessageLimit(userPlan);
        const used = userData.date === new Date().toISOString().split('T')[0] ? userData.count : 0;
        return Math.max(0, limit - used);
    } catch (error) {
        console.error(`Firestore Error: Failed to get remaining messages for user ${userId}. Assuming 0 remaining.`, error);
        return 0;
    }
};

/**
 * Get the next reset date for pro models (first of next month)
 */
export const getProModelsResetInfo = (): { formattedDate: string, daysUntil: number } => {
    const now = new Date();
    const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const diffTime = resetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const formattedDate = resetDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    return { formattedDate, daysUntil: diffDays };
};

/**
 * Get the time when the rate limit will reset (midnight tonight)
 * @returns Object with formatted time and hours/minutes until reset
 */
export const getRateLimitResetTime = (): { formattedTime: string, hoursUntil: number, minutesUntil: number } => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const diffMs = tomorrow.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const formattedTime = tomorrow.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return { formattedTime, hoursUntil: diffHours, minutesUntil: diffMinutes };
};

/**
 * Set the user's plan in Firestore
 */
export const setUserPlan = async (userId: string, newPlan: UserPlan): Promise<void> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, userId);
        const dataToSet: Partial<RateLimitData> = {
            plan: newPlan,
            lastUpdated: serverTimestamp() as Timestamp
        };

        // Initialize Pro model tracking for users being upgraded to Pro
        if (newPlan === 'pro') {
            dataToSet.proModelsUsage = 0;
            dataToSet.proModelsResetDate = getProModelsResetDate();
        }

        // Use merge: true to only update the specified fields,
        // creating the doc if it doesn't exist or preserving existing data
        await setDoc(docRef, dataToSet, { merge: true });
        console.log(`Firestore: Set plan for user ${userId} to ${newPlan}.`);
    } catch (error) {
        console.error(`Firestore Error: Failed to set plan for user ${userId}.`, error);
        throw error; // Re-throw error so the calling component knows it failed
    }
}; 